import React, { useState, useEffect } from "react";
import { Zap, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePortal } from "@/hooks/usePortal";
import { useNavigate } from "react-router-dom";
import CreatePortalModal from "./CreatePortalModal";

interface RequestPortalButtonProps {
  userId: string;
  userName: string;
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

const RequestPortalButton = ({
  userId,
  userName,
  variant = "default",
  size = "default",
  className,
}: RequestPortalButtonProps) => {
  const navigate = useNavigate();
  const { getPortalWithUser, outgoingInvites } = usePortal();
  const [status, setStatus] = useState<"idle" | "invited" | "portal">("idle");
  const [portalId, setPortalId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      setChecking(true);
      
      // Check if portal already exists
      const existingPortal = await getPortalWithUser(userId);
      if (existingPortal) {
        setStatus("portal");
        setPortalId(existingPortal.id);
        setChecking(false);
        return;
      }

      // Check if invite already sent
      const hasPendingInvite = outgoingInvites.some(
        inv => inv.to_user_id === userId && inv.status === "pending"
      );
      if (hasPendingInvite) {
        setStatus("invited");
      }
      
      setChecking(false);
    };

    checkStatus();
  }, [userId, getPortalWithUser, outgoingInvites]);

  const handleClick = () => {
    if (status === "portal" && portalId) {
      navigate(`/portal?open=${portalId}`);
    } else if (status === "idle") {
      setShowModal(true);
    }
  };

  if (checking) {
    return (
      <Button variant={variant} size={size} disabled className={className}>
        <Loader2 className="w-4 h-4 animate-spin" />
      </Button>
    );
  }

  return (
    <>
      <Button
        variant={status === "invited" ? "outline" : variant}
        size={size}
        onClick={handleClick}
        disabled={status === "invited"}
        className={className}
      >
        {status === "portal" ? (
          <>
            <Zap className="w-4 h-4 mr-2" />
            Open Portal
          </>
        ) : status === "invited" ? (
          <>
            <Check className="w-4 h-4 mr-2" />
            Invite Sent
          </>
        ) : (
          <>
            <Zap className="w-4 h-4 mr-2" />
            Request Portal Access
          </>
        )}
      </Button>

      <CreatePortalModal
        open={showModal}
        onClose={() => setShowModal(false)}
        prefilledUserId={userId}
        prefilledUserName={userName}
      />
    </>
  );
};

export default RequestPortalButton;
